import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  AutocompleteInput,
  TextInput,
  NumberInput,
  Create,
  Edit,
  SimpleForm,
  EditButton,
  FunctionField,
  useRecordContext,
  required,
  TopToolbar,
  FilterButton,
  CreateButton,
  useListContext,
  Link,
} from 'react-admin';
import { Tabs, Tab } from '@mui/material';

// ==========================
// স্ট্যাটাস কালার ব্যাজ
// ==========================
const StatusBadge = () => {
  const record = useRecordContext();
  if (!record) return null;
  const colors = { Pending: '#f1c40f', Approved: '#2ecc71', Rejected: '#e74c3c' };
  return (
    <span style={{ color: colors[record.status] || '#ccc', fontWeight: 'bold' }}>
      {record.status}
    </span>
  );
};

// ==========================
// সাপোর্ট টিকেট কলাম — DepositRequest-এ সরাসরি support_ticket ফিল্ড নেই,
// এটা AgentSupportTicket.deposit_request এর reverse relation। তাই
// ReferenceField কাজ করবে না — backend serializer-এ যোগ করা
// `latest_support_ticket` (SerializerMethodField, {id, ticket_number, status})
// অবজেক্ট থেকে সরাসরি লিংক বানানো হচ্ছে।
//
// === পরিবর্তন: এই Link, Datagrid এর rowClick="edit" এর ভেতরে থাকায় ক্লিক
// করলে event bubble হয়ে row-এর rowClick (deposit edit) ও একসাথে ট্রিগার
// হয়ে যাচ্ছিলো — তাই আগে deposit view/edit খুলে যেতো, তারপর back করলে
// ticket page দেখাতো। এখন onClick এ e.stopPropagation() যোগ করে সেই bubble
// বন্ধ করা হলো, যাতে সরাসরি শুধু ticket show পেজেই যায়। ===
// ==========================
const SupportTicketField = () => {
  const record = useRecordContext();
  const ticket = record?.latest_support_ticket;
  if (!ticket) return <span>-</span>;
  return (
    <Link
      to={`/agent-support-tickets/${ticket.id}/show`}
      onClick={(e) => e.stopPropagation()}
    >
      {ticket.ticket_number} ({ticket.status})
    </Link>
  );
};

// ==========================
// === নতুন: পেমেন্ট স্ক্রিনশট ফিল্ড ===
// ইউজার ডিপোজিটের সময় যে স্ক্রিনশট/PDF আপলোড করেছে সেটা দেখায়।
// backend serializer এর `payment_screenshot_url` থেকে লিংক আসে।
//
// size="small"  -> লিস্ট টেবিলে ছোট থাম্বনেইল (৪৮x৪৮)
// size="large"  -> Edit পেজে বড় প্রিভিউ
//
// লিস্টে rowClick="edit" থাকায় ক্লিক করলে row এর edit পেজও খুলে যেতো,
// তাই e.stopPropagation() দেওয়া আছে — ক্লিক করলে শুধু নতুন ট্যাবে ছবি খুলবে।
// ==========================
const PaymentScreenshotField = ({ size = 'small' }) => {
  const record = useRecordContext();
  const url = record?.payment_screenshot_url;

  if (!url) {
    return <span style={{ color: '#999', fontSize: 12 }}>স্ক্রিনশট নেই</span>;
  }

  const isPdf = url.toLowerCase().split('?')[0].endsWith('.pdf');

  if (isPdf) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ color: '#e74c3c', fontWeight: 'bold', fontSize: 13 }}
      >
        📄 PDF দেখুন
      </a>
    );
  }

  const imgStyle =
    size === 'large'
      ? { maxWidth: 320, maxHeight: 420, borderRadius: 10, border: '1px solid #ddd', objectFit: 'contain' }
      : { width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' };

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="বড় করে দেখতে ক্লিক করুন"
    >
      <img src={url} alt="Payment Screenshot" style={imgStyle} />
    </a>
  );
};

// ==========================
// All / Pending / Approved / Rejected ট্যাব ফিল্টার
// ==========================
const statusTabs = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
];

const StatusFilterTabs = () => {
  const { filterValues, setFilters } = useListContext();
  const currentStatus = filterValues.status || '';

  const handleChange = (event, newValue) => {
    const newFilters = { ...filterValues };
    if (newValue === '') delete newFilters.status;
    else newFilters.status = newValue;
    setFilters(newFilters, {});
  };

  return (
    <Tabs value={currentStatus} onChange={handleChange} indicatorColor="primary" textColor="primary" sx={{ mb: 1 }}>
      {statusTabs.map((tab) => (
        <Tab key={tab.value} label={tab.label} value={tab.value} />
      ))}
    </Tabs>
  );
};

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
  </TopToolbar>
);

const depositFilters = [
  <TextInput source="q" label="Search" alwaysOn />,
];

// ==========================
// List
// ==========================
export const DepositRequestList = (props) => (
  <List {...props} filters={depositFilters} actions={<ListActions />}>
    <>
      <StatusFilterTabs />
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="id" />
        <TextField source="order_id" label="Order" />
        <ReferenceField source="user" reference="users" label="Username">
          <TextField source="username" />
        </ReferenceField>
        <ReferenceField source="method" reference="payment-methods" label="Method">
          <TextField source="name" />
        </ReferenceField>
        <NumberField source="amount" label="Amount (BDT)" />
        <NumberField source="bonus_amount" label="Bonus" />
        <TextField source="transaction_id" label="TrxID" />

        {/* === নতুন: পেমেন্ট স্ক্রিনশট কলাম === */}
        <FunctionField label="Screenshot" render={() => <PaymentScreenshotField size="small" />} />

        <ReferenceField source="channel" reference="payment-channels" label="Channel" emptyText="-">
          <TextField source="channel_name" />
        </ReferenceField>
        <ReferenceField source="channel_agent" reference="agents" label="Agent" emptyText="-">
          <TextField source="display_name" />
        </ReferenceField>
        <TextField source="agent_status" label="Agent Status" />

        {/* === পরিবর্তন: ReferenceField এর বদলে SerializerMethodField
            (latest_support_ticket) থেকে সরাসরি লিংক === */}
        <FunctionField label="Support Ticket" render={() => <SupportTicketField />} />

        <FunctionField label="Status" render={() => <StatusBadge />} />
        <BooleanField source="balance_credited" label="Credited" />

        {/* === প্রয়োগকৃত উইথড্র লিমিট মাল্টিপ্লায়ার
            এটা শুধু Approve হওয়ার পরেই সেট হয় (_credit_deposit-এ), তাই Pending
            রিকোয়েস্টে খালি থাকবে। === */}
        <NumberField
          source="applied_multiplier"
          label="প্রয়োগকৃত উইথড্র লিমিট মাল্টিপ্লায়ার"
          emptyText="-"
        />

        <DateField source="created_at" label="Created at" showTime />
        <EditButton />
      </Datagrid>
    </>
  </List>
);

// ==========================
// Create — এখান থেকে তৈরি করলে শুধু Pending রিকোয়েস্ট তৈরি হবে,
// ব্যালেন্স তখনই যোগ হবে না, Approve করলে যোগ হবে
// ==========================
export const DepositRequestCreate = (props) => (
  <Create {...props}>
    <SimpleForm>
      <ReferenceInput source="user" reference="users" label="User">
        <AutocompleteInput optionText="username" validate={required()} />
      </ReferenceInput>

      <ReferenceInput source="method" reference="payment-methods" label="Method">
        <SelectInput optionText="name" validate={required()} />
      </ReferenceInput>

      <ReferenceInput source="channel" reference="payment-channels" label="Channel">
        <SelectInput optionText="channel_name" />
      </ReferenceInput>

      <TextInput source="transaction_id" label="Transaction ID" validate={required()} />
      <NumberInput source="amount" label="Amount (BDT)" validate={required()} />
    </SimpleForm>
  </Create>
);

// ==========================
// Edit — Approve করলে ব্যাকএন্ড লজিক (ব্যালেন্স+বোনাস+কমিশন) চলবে
// ==========================
export const DepositRequestEdit = (props) => (
  <Edit {...props}>
    <SimpleForm>
      {/* === নতুন: ইউজারের দেওয়া পেমেন্ট স্ক্রিনশট (বড় প্রিভিউ) — অ্যাডমিন যাচাই করে Approve/Reject করবে === */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>
          Payment Screenshot (ইউজারের দেওয়া)
        </div>
        <PaymentScreenshotField size="large" />
      </div>

      <ReferenceInput source="user" reference="users" label="User">
        <AutocompleteInput optionText="username" disabled />
      </ReferenceInput>

      <ReferenceInput source="method" reference="payment-methods" label="Method">
        <SelectInput optionText="name" />
      </ReferenceInput>

      <ReferenceInput source="channel" reference="payment-channels" label="Channel">
        <SelectInput optionText="channel_name" />
      </ReferenceInput>

      <TextInput source="transaction_id" label="Transaction ID" />
      <NumberInput source="amount" label="Amount (BDT)" />

      <SelectInput
        source="status"
        label="Status"
        choices={[
          { id: 'Pending', name: 'Pending' },
          { id: 'Approved', name: 'Approved' },
          { id: 'Rejected', name: 'Rejected' },
        ]}
        validate={required()}
      />

      <TextInput source="agent_note" label="Agent Note" disabled fullWidth />
      <NumberInput source="bonus_amount" label="Bonus (auto)" disabled />
      <NumberInput source="applied_multiplier" label="Withdraw Limit Multiplier (auto)" disabled />
      <NumberInput source="referral_commission_amount" label="Referral Commission (auto)" disabled />
      <NumberInput source="agent_commission_amount" label="Agent Commission (auto)" disabled />
    </SimpleForm>
  </Edit>
);